  import React, { useState, useEffect } from 'react';
  import { Users, Plus, Search, Filter, Edit, XCircle, CheckCircle, CalendarDays,  Trash2  } from 'lucide-react';


  import { Link, useNavigate } from 'react-router-dom';
  import API from '../../api';
  import MessageModal from '../smallComponent/MessageModal';
  import { addActivity } from '../../utils/activityService';
import { API_IMAGE_URL, API_IMAGE_URL2 } from '../../lib/api';

  export default function EmployeeManagement() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [formData, setFormData] = useState({
      email: '', password: '', full_name: '', employee_id: '',
      department: '', position: '', salary: '', phone: '',
      hire_date: '', role: 'employee'
    });

    const navigate = useNavigate();

    const [modal, setModal] = useState({ show: false, message: '', type: 'info' });

    useEffect(() => {
      fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
      try {
        const res = await API.get('/employees')
        
        setEmployees(res.data.employees || []);
      } catch (error) {
        showModal('Failed to fetch employees', 'error');
      } finally {
        setLoading(false);
      }
    };

    const resetForm = () => {
      setFormData({
        email: '', password: '', full_name: '', employee_id: '',
        department: '', position: '', salary: '', phone: '',
        hire_date: '', role: 'employee'
      });
    };

    const showModal = (message, type = 'info') => {
      setModal({ show: true, message, type });
    };

    const closeModal = () => setModal({ ...modal, show: false });

    const handleAddOrUpdate = async (e) => {
      e.preventDefault();
      try {
        if (editingEmployee) {
          const updateData = { ...formData };
          if (!updateData.password) delete updateData.password;
          await API.put(`/employees/${editingEmployee.id}`, updateData);
          showModal('Employee updated successfully', 'success');
        } else {
          await API.post('/auth/register', formData);
          showModal('Employee added successfully', 'success');
        }
        resetForm();
        setEditingEmployee(null);
        setShowAddForm(false);
        fetchEmployees();
      } catch (error) {
        console.error(error);
        showModal('Operation failed', 'error');
      }
    };

    const handleDeactivate = async (id) => {
      try {
        await API.put(`/employees/${id}/deactivate`);
        showModal('Employee deactivated successfully', 'success');
        fetchEmployees();
      } catch (error) {
        console.error(error);
        showModal('Failed to deactivate employee', 'error');
      }
    };

    const handleActivate = async (id) => {
      try {
        const res = await API.put(`/auth/${id}/activate`);
        const newId = res.data.employee_id;
        await addActivity(id, `Activated employee. Assigned ID: ${newId}`, 'system');
        showModal(`Employee activated. ID: ${newId}`, 'success');
        fetchEmployees();
      } catch (error) {
        console.error(error);
        const msg = error.response?.data?.message || 'Failed to activate employee';
        showModal(msg, 'error');
      }
    };

    const departments = [...new Set(employees.map(emp => emp.department))];
    const filteredEmployees = employees.filter(emp =>
      (emp.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (!departmentFilter || emp.department === departmentFilter)
    );

    const handleDelete = async (id) => {
  if (!window.confirm('Are you sure you want to delete this employee?')) return;

  try {
    await API.delete(`/employees/${id}`);
    showModal('Employee deleted successfully', 'success');
    fetchEmployees();
  } catch (error) {
    console.error(error);
    showModal('Failed to delete employee', 'error');
  }
};

    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Employee Management</h2>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Employee
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
            <input
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-300"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              className="px-3 py-2 border border-gray-300 rounded"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow">
            <table className="w-full text-left">
              <thead className="bg-gray-100 text-sm text-gray-700">
                <tr>
                  
                  <th className="px-6 py-3">
                    Photo
                  </th>
                  <th className="px-6 py-3">Employee</th>
                  <th className="px-6 py-3">Department</th>
                  <th className="px-6 py-3">Position</th>
                  <th className="px-6 py-3">Salary</th>
                  <th className="px-6 py-3">Hire Date</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-gray-200">
                {filteredEmployees.map(emp => (
                  <tr 
                    key={emp.id}
  onClick={() => navigate(`/dashboard/employee/view/${emp.id}`)}
  className="hover:bg-gray-100 cursor-pointer"
                  >

                    <td className="px-6 py-4">
                     
                      <img
                        src={emp.profile_image ? `${API_IMAGE_URL2}${emp.profile_image}` : '/default-profile.png'}
                        alt={emp.full_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="font-medium text-gray-900">{emp.full_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      
                      <div className="text-gray-500 text-sm">{emp.employee_id} <br/>{emp.phone} <br/> {emp.email}</div>
                    </td>
                    <td className="px-6 py-4">{emp.department}</td>
                    <td className="px-6 py-4">{emp.position}</td>
                    <td className="px-6 py-4">₹{emp.salary}</td>
                    <td className="px-6 py-4">{new Date(emp.hire_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full font-semibold ${emp.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {emp.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
<td className="px-6 py-4 space-y-2" onClick={(e) => e.stopPropagation()}>
  <div className="flex items-center gap-2">
    <button
      className="px-3 py-1 bg-blue-100 text-blue-700 rounded inline-flex items-center gap-1"
      onClick={() => {
        setEditingEmployee(emp);
        setFormData({
          ...emp,
          password: '',
          hire_date: emp.hire_date
            ? new Date(emp.hire_date).toISOString().split('T')[0]
            : ''
        });
        setShowAddForm(true);
      }}
    >
      <Edit className="w-4 h-4" />
    </button>

    {emp.is_active ? (
      <button
        className="px-3 py-1 bg-red-100 text-red-700 rounded inline-flex items-center gap-1"
        onClick={() => handleDeactivate(emp.id)}
      >
        <XCircle className="w-4 h-4" />
      </button>
    ) : (
      <button
        className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded inline-flex items-center gap-1"
        onClick={() => handleActivate(emp.id)}
      >
        <CheckCircle className="w-4 h-4" />
      </button>
    )}
  </div>

  <div className="flex items-center gap-2">
    <Link
      to={`/dashboard/employee/${emp.id}/attendance`}
      className="px-3 py-1 bg-green-100 text-green-700 rounded inline-flex items-center gap-1"
    >
      <CalendarDays className="w-4 h-4" />
    </Link>

    <button
      className="px-3 py-1 bg-gray-100 text-gray-700 rounded inline-flex items-center gap-1"
      onClick={() => handleDelete(emp.id)}
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
</td>


                  </tr>
                ))}
              </tbody>
            </table>
            {filteredEmployees.length === 0 && (
              <div className="text-center py-10 text-gray-500">No employees found.</div>
            )}
          </div>
        )}

        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xl overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{editingEmployee ? 'Edit' : 'Add'} Employee</h3>
                <button
                  className="text-gray-600 hover:text-black"
                  onClick={() => { setShowAddForm(false); resetForm(); setEditingEmployee(null); }}
                >✕</button>
              </div>
              <form onSubmit={handleAddOrUpdate} className="space-y-4">
  {['full_name', 'email', 'phone', 'position', 'salary', 'hire_date'].map(field => (
    <div key={field}>
      <label className="block text-sm font-medium mb-1 capitalize">{field.replace('_', ' ')}</label>
      <input
        type={field === 'salary' ? 'number' : field === 'hire_date' ? 'date' : 'text'}
        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-300"
        value={formData[field] || ''}
        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
        required
      />
    </div>
  ))}

  {/* Department dropdown */}
  <div>
    <label className="block text-sm font-medium mb-1">Department</label>
    <select
      name="department"
      value={formData.department}
      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
      required
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
    >
      <option value="">Select Department</option>
      <option value="Sales">Sales</option>
      <option value="Developer">Developer</option>
      <option value="Management">Management</option>
    </select>
  </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Password {editingEmployee ? '(leave blank to keep unchanged)' : ''}
                  </label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-300"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingEmployee}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 text-gray-700 hover:text-black"
                    onClick={() => { setShowAddForm(false); resetForm(); setEditingEmployee(null); }}
                  >Cancel</button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >{editingEmployee ? 'Update' : 'Add'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <MessageModal
          show={modal.show}
          message={modal.message}
          type={modal.type}
          onClose={closeModal}
        />
      </div>
    );
  }
